import { Text } from "@clipboard-health/ui-components";
import { Stack, useTheme } from "@mui/material";
import { useMemo } from "react";

import { GroupedList } from "../../components/GroupedList";
import { ListLoadingState } from "../../components/ListLoadingState";
import { WorkplacesListEmptySearchResult } from "./EmptySearchResult";
import { WorkplacesListEmptyState } from "./EmptyState";
import { WorkplaceListItem, type WorkplaceListItemProps } from "./Item";

enum GroupId {
  WITH_SHIFTS_THAT_MATCH_YOUR_FILTERS = "0",
  ALL_OTHER_WORKPLACES = "1",
}

export interface WorkplaceResultGroup {
  workplaces: WorkplaceListItemProps[];
  totalCount: number;
}

interface WorkplacesListProps {
  isEmpty: boolean;
  isError: boolean;
  isLoading: boolean;
  isSuccess: boolean;
  scrollRef: React.RefObject<HTMLDivElement>;
  searchTerm: string;
  distanceFilter: number;
  workplacesMatchingFiltersGroup: WorkplaceResultGroup;
  allOtherWorkplacesGroup: WorkplaceResultGroup;
  isLoadingMore?: boolean;
  onLoadMore?: () => void;
}

type GroupedWorkplace = WorkplaceListItemProps & {
  groupId: string;
};

export function WorkplacesList(props: WorkplacesListProps) {
  const {
    isError,
    isLoading,
    isSuccess,
    scrollRef,
    searchTerm,
    isEmpty,
    distanceFilter,
    workplacesMatchingFiltersGroup,
    allOtherWorkplacesGroup,
    isLoadingMore,
    onLoadMore,
  } = props;
  const theme = useTheme();

  const groupedItems = useMemo(
    () => [
      ...workplacesMatchingFiltersGroup.workplaces.map((workplace) => ({
        ...workplace,
        groupId: GroupId.WITH_SHIFTS_THAT_MATCH_YOUR_FILTERS,
      })),
      ...allOtherWorkplacesGroup.workplaces.map((workplace) => ({
        ...workplace,
        groupId: GroupId.ALL_OTHER_WORKPLACES,
      })),
    ],
    [workplacesMatchingFiltersGroup.workplaces, allOtherWorkplacesGroup.workplaces]
  );

  const groupMetadataById = new Map([
    [
      GroupId.WITH_SHIFTS_THAT_MATCH_YOUR_FILTERS,
      {
        label: "With shifts that match your filters",
        count: workplacesMatchingFiltersGroup.totalCount,
      },
    ],
    [
      GroupId.ALL_OTHER_WORKPLACES,
      {
        label: "All other workplaces",
        count: allOtherWorkplacesGroup.totalCount,
      },
    ],
  ]);

  if (!isLoading && isError) {
    return <Text align="center">Error loading workplaces. Please try again.</Text>;
  }

  if (!isLoading && isSuccess && isEmpty && searchTerm === "") {
    return <WorkplacesListEmptyState distanceFilter={distanceFilter} />;
  }

  if (!isLoading && isSuccess && isEmpty && searchTerm !== "") {
    return <WorkplacesListEmptySearchResult searchTerm={searchTerm} />;
  }

  return (
    <GroupedList
      customScrollParentRef={scrollRef}
      items={groupedItems}
      isLoadingMore={isLoadingMore}
      groupBy={(item: GroupedWorkplace) => item.groupId}
      backgroundColor={theme.palette.common.white}
      renderGroupHeader={(groupId: string) => {
        const totalCount = groupMetadataById.get(groupId as GroupId)?.count ?? 0;
        const label = groupMetadataById.get(groupId as GroupId)?.label;

        if (!label) {
          return null;
        }

        return (
          <Stack direction="row" gap={2}>
            <Text variant="body1" color={(theme) => theme.palette.text.tertiary}>
              {label}
            </Text>
            <Text variant="body1" color={(theme) => theme.palette.text.secondary}>
              {totalCount}
            </Text>
          </Stack>
        );
      }}
      renderItem={(workplace: GroupedWorkplace) => {
        const index = groupedItems.findIndex(
          (item) => item.workplaceId === workplace.workplaceId && item.groupId === workplace.groupId
        );

        const isVeryLastItem = index === groupedItems.length - 1;
        const showDividerAfter =
          !isVeryLastItem && groupedItems[index + 1].groupId !== workplace.groupId;

        return <WorkplaceListItem {...workplace} showDividerAfter={showDividerAfter} />;
      }}
      endReached={() => {
        onLoadMore?.();
      }}
      scrollerTestId="workplaces-list-scroll-container"
      contentPlaceholder={
        <ListLoadingState
          texts={[
            "Finding workplaces nearby",
            "Analyzing workplace schedules",
            "Workplaces are on the way",
            "Digging deep into facility schedules",
            "Searching high and low for the best workplaces nearby",
            "Unlocking your next great opportunity",
          ]}
        />
      }
      showPlaceholder={isLoading}
    />
  );
}
